import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { 
  Building2, Upload, FileText, Image, FileSpreadsheet, Check, AlertCircle, AlertTriangle, Info,
  LayoutDashboard, List, ClipboardCheck, FileBarChart, CreditCard, Shield, LogOut, Menu, X,
  ArrowLeftRight, ChevronDown, ChevronUp, Copy, Calendar
} from "lucide-react";

export default function UploadPage() {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recentUploads, setRecentUploads] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [duplicateSummary, setDuplicateSummary] = useState(null);
  const [showDuplicateDetails, setShowDuplicateDetails] = useState(false);
  
  const lastProgressUpdate = useRef(0);
  const progressTimeoutRef = useRef(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchAccounts();
    fetchRecentStatements();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts`, { headers, withCredentials: true });
      setAccounts(response.data.filter(a => a.is_active));
      if (response.data.length > 0) {
        setSelectedAccount(response.data[0].account_id);
      }
    } catch (error) {
      console.error("Error fetching accounts:", error);
    }
  };

  const fetchRecentStatements = async () => {
    try {
      const response = await axios.get(`${API}/statements?limit=5`, { headers, withCredentials: true });
      setRecentUploads(response.data.slice(0, 5));
    } catch (error) {
      console.error("Error fetching statements:", error);
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!selectedAccount) {
      toast.error("Please select a bank account first");
      return;
    }

    if (acceptedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    setDuplicateSummary(null);

    for (const file of acceptedFiles) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("account_id", selectedAccount);

      try {
        const response = await axios.post(`${API}/statements/upload`, formData, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            const now = Date.now();
            if (now - lastProgressUpdate.current >= 50 || progress === 100) {
              lastProgressUpdate.current = now;
              if (progressTimeoutRef.current) {
                clearTimeout(progressTimeoutRef.current);
              }
              setUploadProgress(progress);
            } else if (!progressTimeoutRef.current) {
              progressTimeoutRef.current = setTimeout(() => {
                setUploadProgress(progress);
                progressTimeoutRef.current = null;
              }, 50);
            }
          }
        });

        // Show success with import summary
        const { transactions_extracted, duplicates_skipped, duplicate_details, transactions_needing_review } = response.data;
        
        if (duplicates_skipped > 0) {
          setDuplicateSummary({
            filename: file.name,
            extracted: transactions_extracted,
            skipped: duplicates_skipped,
            details: duplicate_details || [],
            needsReview: transactions_needing_review
          });
          
          toast.warning(
            `${duplicates_skipped} duplicate transaction${duplicates_skipped > 1 ? 's' : ''} skipped`,
            { duration: 5000 }
          );
        } else {
          toast.success(`Extracted ${transactions_extracted} transactions from ${file.name}`);
        }
        
        if (transactions_needing_review > 0) {
          toast.info(`${transactions_needing_review} transactions need review`, {
            action: {
              label: "Review Now",
              onClick: () => navigate("/review")
            }
          });
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}: ${error.response?.data?.detail || error.message}`);
      }
    }

    if (progressTimeoutRef.current) {
      clearTimeout(progressTimeoutRef.current);
      progressTimeoutRef.current = null;
    }
    
    setUploading(false);
    setUploadProgress(0);
    fetchRecentStatements();
  }, [selectedAccount, headers, navigate]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/webp': ['.webp']
    },
    multiple: true,
    disabled: uploading
  });

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'csv':
      case 'excel':
        return <FileSpreadsheet className="w-5 h-5 text-emerald-500" />;
      case 'pdf':
        return <FileText className="w-5 h-5 text-red-500" />;
      case 'image':
        return <Image className="w-5 h-5 text-blue-500" />;
      default:
        return <FileText className="w-5 h-5 text-zinc-500" />;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const NavLink = ({ to, icon: Icon, children, active }) => (
    <Link
      to={to}
      className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
        active ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
      }`}
    >
      <Icon className="w-5 h-5" />
      <span className="text-sm font-medium">{children}</span>
    </Link>
  );

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#121214] border-r border-zinc-800 hidden lg:flex flex-col">
        <div className="p-6 border-b border-zinc-800">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FCD34D] flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#1C1917]" />
            </div>
            <div>
              <h1 className="font-semibold text-white">BankFlow Control</h1>
              <p className="text-xs text-zinc-500">Financial Analysis</p>
            </div>
          </Link>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          <NavLink to="/dashboard" icon={LayoutDashboard}>Dashboard</NavLink>
          <NavLink to="/upload" icon={Upload} active>Upload Statements</NavLink>
          <NavLink to="/transactions" icon={List}>Transactions</NavLink>
          <NavLink to="/transfers" icon={ArrowLeftRight}>Transfer Detection</NavLink>
          <NavLink to="/reconciliation" icon={Calendar}>Weekly Summary</NavLink>
          <NavLink to="/review" icon={ClipboardCheck}>Review Queue</NavLink>
          <NavLink to="/reports" icon={FileBarChart}>Reports</NavLink>
          <NavLink to="/accounts" icon={CreditCard}>Bank Accounts</NavLink>
          {user?.role === 'admin' && <NavLink to="/admin" icon={Shield}>Admin Settings</NavLink>}
        </nav>
        
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-sm font-medium text-white">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-800" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#121214] border-b border-zinc-800">
        <div className="flex items-center justify-between px-4 h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#FCD34D] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#1C1917]" />
            </div>
            <span className="font-semibold text-white">BankFlow</span>
          </Link>
          <Button variant="ghost" size="icon" className="text-zinc-400" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
        {mobileMenuOpen && (
          <nav className="absolute top-16 left-0 right-0 bg-[#121214] border-b border-zinc-800 p-4 space-y-1">
            <NavLink to="/dashboard" icon={LayoutDashboard}>Dashboard</NavLink>
            <NavLink to="/upload" icon={Upload} active>Upload</NavLink>
            <NavLink to="/transactions" icon={List}>Transactions</NavLink>
            <NavLink to="/review" icon={ClipboardCheck}>Review</NavLink>
            <NavLink to="/reports" icon={FileBarChart}>Reports</NavLink>
            <NavLink to="/accounts" icon={CreditCard}>Accounts</NavLink>
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-4 md:p-8">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-white">Upload Bank Statements</h1>
            <p className="text-zinc-400 text-sm mt-1">Upload PDFs, images, CSV, or Excel files</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Upload Area */}
            <div className="lg:col-span-2 space-y-6">
              {/* Account Selection */}
              <Card className="bg-[#121214] border-zinc-800">
                <CardContent className="p-6">
                  <label className="text-sm font-medium text-zinc-300 mb-3 block">Select Bank Account</label>
                  <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                    <SelectTrigger className="bg-zinc-900/50 border-zinc-800 text-white" data-testid="upload-account-select">
                      <SelectValue placeholder="Select an account" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121214] border-zinc-800">
                      {accounts.map(acc => (
                        <SelectItem key={acc.account_id} value={acc.account_id} className="text-white hover:bg-zinc-800">
                          {acc.name} {acc.bank_name && `(${acc.bank_name})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>

              {/* Dropzone */}
              <Card className="bg-[#121214] border-zinc-800">
                <CardContent className="p-6">
                  <div
                    {...getRootProps()}
                    className={`dropzone p-12 text-center cursor-pointer ${isDragActive ? 'active border-[#FCD34D] bg-[#FCD34D]/5' : ''} ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid="upload-dropzone"
                  >
                    <input {...getInputProps()} data-testid="file-input" />
                    <div className="w-16 h-16 rounded-2xl bg-zinc-800/50 flex items-center justify-center mx-auto mb-4">
                      <Upload className={`w-8 h-8 ${isDragActive ? 'text-[#FCD34D]' : 'text-zinc-500'}`} />
                    </div>
                    {isDragActive ? (
                      <p className="text-[#FCD34D] font-medium">Drop files here...</p>
                    ) : (
                      <>
                        <p className="text-white font-medium mb-2">Drag & drop bank statements here</p>
                        <p className="text-zinc-500 text-sm">or click to browse</p>
                      </>
                    )}
                    <div className="flex flex-wrap justify-center gap-2 mt-4">
                      <span className="text-xs text-zinc-500 px-2 py-1 bg-zinc-800/50 rounded">PDF</span>
                      <span className="text-xs text-zinc-500 px-2 py-1 bg-zinc-800/50 rounded">CSV</span>
                      <span className="text-xs text-zinc-500 px-2 py-1 bg-zinc-800/50 rounded">Excel</span>
                      <span className="text-xs text-zinc-500 px-2 py-1 bg-zinc-800/50 rounded">PNG</span>
                      <span className="text-xs text-zinc-500 px-2 py-1 bg-zinc-800/50 rounded">JPG</span>
                    </div>
                  </div>

                  {uploading && (
                    <div className="mt-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-zinc-400">Uploading & Processing...</span>
                        <span className="text-sm text-[#FCD34D] font-mono">{uploadProgress}%</span>
                      </div>
                      <Progress value={uploadProgress} className="h-2 bg-zinc-800" />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Duplicate Import Summary */}
              {duplicateSummary && (
                <Card className="bg-[#121214] border-amber-500/30" data-testid="duplicate-summary-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                      Duplicate Import Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4 mb-4">
                      <p className="text-zinc-300 text-sm mb-3">
                        <span className="font-medium text-white">{duplicateSummary.filename}</span> was processed with the following results:
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div className="text-center p-3 bg-zinc-900/50 rounded-lg">
                          <p className="text-2xl font-bold text-emerald-500">{duplicateSummary.extracted}</p>
                          <p className="text-xs text-zinc-500">Imported</p>
                        </div>
                        <div className="text-center p-3 bg-zinc-900/50 rounded-lg">
                          <p className="text-2xl font-bold text-amber-500">{duplicateSummary.skipped}</p>
                          <p className="text-xs text-zinc-500">Skipped (Duplicates)</p>
                        </div>
                        {duplicateSummary.needsReview > 0 && (
                          <div className="text-center p-3 bg-zinc-900/50 rounded-lg">
                            <p className="text-2xl font-bold text-blue-500">{duplicateSummary.needsReview}</p>
                            <p className="text-xs text-zinc-500">Need Review</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Duplicate Details */}
                    {duplicateSummary.details.length > 0 && (
                      <div>
                        <Button
                          variant="ghost"
                          className="w-full justify-between text-zinc-400 hover:text-white hover:bg-zinc-800/50 mb-2"
                          onClick={() => setShowDuplicateDetails(!showDuplicateDetails)}
                          data-testid="toggle-duplicate-details"
                        >
                          <span className="flex items-center gap-2">
                            <Info className="w-4 h-4" />
                            View Skipped Transactions
                          </span>
                          {showDuplicateDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                        
                        {showDuplicateDetails && (
                          <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="duplicate-details-list">
                            {duplicateSummary.details.map((dup, index) => (
                              <div key={index} className="p-3 bg-zinc-900/50 rounded-lg border border-zinc-800">
                                <div className="flex justify-between items-start mb-2">
                                  <p className="text-sm text-white truncate flex-1 mr-2">{dup.new_transaction.description}</p>
                                  <span className="text-xs text-zinc-500 whitespace-nowrap">
                                    {new Date(dup.new_transaction.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-mono">
                                    {dup.new_transaction.amount_in > 0 ? (
                                      <span className="text-emerald-500">+{formatCurrency(dup.new_transaction.amount_in)}</span>
                                    ) : (
                                      <span className="text-red-500">-{formatCurrency(dup.new_transaction.amount_out)}</span>
                                    )}
                                  </span>
                                  <span className="text-xs text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded">
                                    {dup.reason}
                                  </span>
                                </div>
                                {dup.existing_transaction_id && (
                                  <p className="text-xs text-zinc-500 mt-2 flex items-center gap-1">
                                    <Copy className="w-3 h-3" />
                                    Matches: {dup.existing_transaction_id}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex gap-3 mt-4">
                      <Button
                        variant="outline"
                        className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                        onClick={() => setDuplicateSummary(null)}
                      >
                        Dismiss
                      </Button>
                      <Button
                        className="flex-1 bg-[#FCD34D] text-[#1C1917] hover:bg-[#FBBF24]"
                        onClick={() => navigate("/transactions")}
                      >
                        View All Transactions
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Tips */}
              <Card className="bg-[#121214] border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg text-white">Upload Tips</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {[
                      "CSV and Excel files provide the best extraction accuracy",
                      "For PDF statements, ensure text is selectable (not scanned images)",
                      "Screenshot images will be processed with AI-powered OCR",
                      "Duplicate transactions are automatically detected and skipped",
                      "Low-confidence extractions will appear in the Review Queue"
                    ].map((tip, index) => (
                      <li key={index} className="flex items-start gap-3 text-sm text-zinc-400">
                        <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Recent Uploads */}
            <div>
              <Card className="bg-[#121214] border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg text-white">Recent Uploads</CardTitle>
                </CardHeader>
                <CardContent>
                  {recentUploads.length > 0 ? (
                    <div className="space-y-3">
                      {recentUploads.map((stmt) => (
                        <div key={stmt.statement_id} className="p-3 bg-zinc-900/50 rounded-lg">
                          <div className="flex items-center gap-3">
                            {getFileIcon(stmt.file_type)}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">{stmt.filename}</p>
                              <p className="text-xs text-zinc-500">
                                {stmt.total_transactions} transactions · {stmt.extraction_method}
                              </p>
                            </div>
                          </div>
                          {stmt.transactions_needing_review > 0 && (
                            <div className="mt-2 flex items-center gap-2 text-xs text-amber-500">
                              <AlertCircle className="w-3 h-3" />
                              <span>{stmt.transactions_needing_review} need review</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-zinc-500 py-8 text-sm">No statements uploaded yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
